/** Hoja inferior de cuenta: avatar, datos y accesos (design.md §22.7).
 *
 * Se abre desde el avatar del header; cierra con Escape, velo o X y
 * devuelve el foco al disparador.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from './Avatar';
import { ConfirmDialog } from './ConfirmDialog';
import { MobileCard } from './MobileCard';
import { MobileIcon } from './MobileIcon';
import { useMobileAuth } from '@/state/MobileAuthContext';

type AccountSheetProps = {
  open: boolean;
  onClose: () => void;
};

export function AccountSheet({ open, onClose }: AccountSheetProps) {
  const navigate = useNavigate();
  const { user, tenantName, logout } = useMobileAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement;
    closeRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKey);
      if (previousFocus.current instanceof HTMLElement) previousFocus.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const fullName = user?.full_name?.trim() || 'Mi cuenta';
  const email = user?.email?.trim() || '';
  const company = tenantName ?? '';

  function go(path: string) {
    onClose();
    navigate(path);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cuenta"
      className="fixed inset-0 z-50 flex items-end justify-center"
    >
      <div className="absolute inset-0 bg-bg-overlay" onClick={onClose} aria-hidden="true" />

      <div className="relative w-full max-w-md rounded-t-card bg-white p-5 shadow-card safe-area-bottom">
        <div className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-border-dark" />

        <div className="mb-4 mt-2 flex items-center gap-3">
          <Avatar name={fullName} size={48} />
          <div className="min-w-0 flex-1">
            <p title={fullName} className="truncate text-base font-semibold text-text-primary">{fullName}</p>
            {email !== '' && (
              <p title={email} className="truncate text-sm text-text-secondary">{email}</p>
            )}
            {company !== '' && (
              <p title={company} className="truncate text-xs text-text-tertiary">{company}</p>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-bg-tertiary"
          >
            <MobileIcon name="close" className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <MobileCard
            variant="interactive"
            title="Mi perfil"
            description="Nombre, correo y empresa activa"
            icon="person"
            iconColor="primary"
            onClick={() => go('/m/profile')}
          />
          <MobileCard
            variant="interactive"
            title="Cerrar sesión"
            description="Salir de la aplicación en este dispositivo"
            icon="logout"
            iconColor="error"
            onClick={() => setConfirmLogout(true)}
          />
        </div>
      </div>

      {confirmLogout && (
        <ConfirmDialog
          title="¿Cerrar sesión?"
          message="Se cierra la sesión actual en este dispositivo."
          confirmLabel="Cerrar sesión"
          onConfirm={() => void logout()}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </div>
  );
}
